import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { 
  X,
  Save,
  AlertCircle,
  Upload,
  Plus,
  Trash2,
  ImageIcon,
  FileImage,
  Bold,
  Italic,
  List,
  Link,
  Type
} from "lucide-react";

// Simple Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, minHeight = "120px" }) => {
  const editorRef = useRef(null);

  const formatText = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  return (
    <div className="border border-gray-300 rounded-md">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => formatText('bold')}
          className="p-1 h-8 w-8"
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => formatText('italic')}
          className="p-1 h-8 w-8"
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => formatText('insertUnorderedList')}
          className="p-1 h-8 w-8"
          title="Bullet List"
        >
          <List className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 mx-1"></div>
        <select
          onChange={(e) => formatText('formatBlock', e.target.value)}
          className="text-sm border-0 bg-transparent"
          defaultValue=""
        >
          <option value="">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-3 min-h-[120px] focus:outline-none"
        style={{ minHeight }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

const ProductForm = ({ product, categories, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    short_description: "",
    sku: "",
    price: "",
    regular_price: "",
    sale_price: "",
    stock_status: "instock",
    stock_quantity: "",
    categories: [],
    images: [],
    featured: false,
    weight: "",
    dimensions: { length: "", width: "", height: "" },
    status: "publish",
    has_metal_options: false,
    metal_options: []
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (product && isEditing) {
      setFormData({
        name: product.name || "",
        slug: product.slug || "",
        description: product.description || "",
        short_description: product.short_description || "",
        sku: product.sku || "",
        price: product.price || "",
        regular_price: product.regular_price || "",
        sale_price: product.sale_price || "",
        stock_status: product.stock_status || "instock",
        stock_quantity: product.stock_quantity || "",
        categories: product.categories || [],
        images: product.images || [],
        featured: product.featured || false,
        weight: product.weight || "",
        dimensions: product.dimensions || { length: "", width: "", height: "" },
        status: product.status || "publish",
        has_metal_options: product.has_metal_options || false,
        metal_options: product.metal_options || []
      });
    }
  }, [product, isEditing]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDimensionChange = (dimension, value) => {
    setFormData(prev => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [dimension]: value
      }
    }));
  };

  const addImage = () => {
    if (imageUrl.trim()) {
      const newImage = {
        id: Date.now().toString(),
        src: imageUrl.trim(),
        alt: formData.name || "Product image",
        position: formData.images.length
      };
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, newImage]
      }));
      setImageUrl("");
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image file must be less than 10MB');
      return;
    }

    setUploadingImage(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Get auth token - using adminToken to match AdminProductsPage
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload image');
      }

      const result = await response.json();

      // Add uploaded image to product images
      setFormData(prev => {
        const newImage = {
          id: Date.now().toString(),
          src: `${process.env.REACT_APP_BACKEND_URL}${result.url}`,
          alt: prev.name || "Product image",
          position: prev.images.length
        };
        return {
          ...prev,
          images: [...prev.images, newImage]
        };
      });

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  const addCategory = (categoryId) => {
    const category = categories.find(cat => cat.woocommerce_id.toString() === categoryId);
    if (category && !formData.categories.find(cat => cat.id === category.woocommerce_id)) {
      const newCategory = {
        id: category.woocommerce_id,
        name: category.name,
        slug: category.slug
      };
      
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory]
      }));
    }
  };

  const removeCategory = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(cat => cat.id !== categoryId)
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleNameChange = (name) => {
    handleInputChange('name', name);
    if (!isEditing || !formData.slug) {
      handleInputChange('slug', generateSlug(name));
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Product name is required");
      return false;
    }
    if (!formData.price && !formData.regular_price) {
      setError("Product price is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        woocommerce_id: isEditing ? product.woocommerce_id : Date.now(), // Generate ID for new products
        created_at: isEditing ? product.created_at : new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      await onSave(submitData);
    } catch (err) {
      setError(err.message || "Error saving product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <h2 className="text-2xl font-bold text-gray-900">
              {isEditing ? "Edit Product" : "Add New Product"}
            </h2>
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-6 space-y-6">
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-600">{error}</AlertDescription>
              </Alert>
            )}

            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleNameChange(e.target.value)}
                      placeholder="Enter product name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slug
                    </label>
                    <Input
                      value={formData.slug}
                      onChange={(e) => handleInputChange('slug', e.target.value)}
                      placeholder="product-slug"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Short Description
                  </label>
                  <RichTextEditor
                    value={formData.short_description}
                    onChange={(value) => handleInputChange('short_description', value)}
                    placeholder="Brief product description"
                    minHeight="120px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Description
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => handleInputChange('description', value)}
                    placeholder="Detailed product description"
                    minHeight="200px"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pricing & Inventory */}
            <Card>
              <CardHeader>
                <CardTitle>Pricing & Inventory</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Regular Price *
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.regular_price}
                      onChange={(e) => handleInputChange('regular_price', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sale Price
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.sale_price}
                      onChange={(e) => handleInputChange('sale_price', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SKU
                    </label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => handleInputChange('sku', e.target.value)}
                      placeholder="Product SKU"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stock Status
                    </label>
                    <Select 
                      value={formData.stock_status} 
                      onValueChange={(value) => handleInputChange('stock_status', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="instock">In Stock</SelectItem>
                        <SelectItem value="outofstock">Out of Stock</SelectItem>
                        <SelectItem value="onbackorder">On Backorder</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stock Quantity
                    </label>
                    <Input
                      type="number"
                      value={formData.stock_quantity}
                      onChange={(e) => handleInputChange('stock_quantity', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="featured" className="text-sm font-medium text-gray-700">
                    Featured Product
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Metal Type Options */}
            <Card>
              <CardHeader>
                <CardTitle>Metal Type Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="has_metal_options"
                    checked={formData.has_metal_options}
                    onChange={(e) => {
                      handleInputChange('has_metal_options', e.target.checked);
                      if (!e.target.checked) {
                        handleInputChange('metal_options', []);
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="has_metal_options" className="text-sm font-medium text-gray-700">
                    This product has metal type options
                  </label>
                </div>

                {formData.has_metal_options && (
                  <div className="space-y-3 p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700 mb-3">
                      Select the metal types available for this product:
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="sterling_silver"
                        checked={formData.metal_options.includes('sterling_silver')}
                        onChange={(e) => {
                          const newMetalOptions = e.target.checked
                            ? [...formData.metal_options, 'sterling_silver']
                            : formData.metal_options.filter(opt => opt !== 'sterling_silver');
                          handleInputChange('metal_options', newMetalOptions);
                        }}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="sterling_silver" className="text-sm font-medium text-gray-900">
                        Sterling Silver
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="gold_plated"
                        checked={formData.metal_options.includes('gold_plated')}
                        onChange={(e) => {
                          const newMetalOptions = e.target.checked
                            ? [...formData.metal_options, 'gold_plated']
                            : formData.metal_options.filter(opt => opt !== 'gold_plated');
                          handleInputChange('metal_options', newMetalOptions);
                        }}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor="gold_plated" className="text-sm font-medium text-gray-900">
                        Sterling Silver Gold Plated
                      </label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Add Category
                  </label>
                  <Select onValueChange={addCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem 
                          key={category.woocommerce_id} 
                          value={category.woocommerce_id.toString()}
                        >
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.categories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Selected Categories
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {formData.categories.map((category) => (
                        <Badge key={category.id} variant="outline" className="flex items-center gap-1">
                          {category.name}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 hover:bg-red-100"
                            onClick={() => removeCategory(category.id)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Images */}
            <Card>
              <CardHeader>
                <CardTitle>Product Images</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* File Upload Option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Image File
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <Button 
                      type="button" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {uploadingImage ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Choose Image File
                        </>
                      )}
                    </Button>
                    <span className="text-sm text-gray-500">
                      Supports JPG, PNG, GIF, WebP (max 10MB)
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-sm text-gray-500">OR</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>

                {/* URL Input Option */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add Image from URL
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="Enter image URL"
                      className="flex-1"
                    />
                    <Button type="button" onClick={addImage}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add URL
                    </Button>
                  </div>
                </div>

                {formData.images.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Images ({formData.images.length})
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={image.id} className="relative">
                          <div className="aspect-square bg-gray-100 rounded border overflow-hidden">
                            <img
                              src={image.src}
                              alt={image.alt}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = "https://placehold.co/100/100";
                              }}
                            />
                          </div>
                          {index === 0 && (
                            <Badge className="absolute top-1 left-1 bg-green-600 text-white text-xs">
                              Primary
                            </Badge>
                          )}
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 p-0"
                            onClick={() => removeImage(image.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      The first image will be used as the primary product image
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Physical Properties */}
            <Card>
              <CardHeader>
                <CardTitle>Physical Properties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Weight
                    </label>
                    <Input
                      value={formData.weight}
                      onChange={(e) => handleInputChange('weight', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Length
                    </label>
                    <Input
                      value={formData.dimensions.length}
                      onChange={(e) => handleDimensionChange('length', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Width
                    </label>
                    <Input
                      value={formData.dimensions.width}
                      onChange={(e) => handleDimensionChange('width', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Height
                    </label>
                    <Input
                      value={formData.dimensions.height}
                      onChange={(e) => handleDimensionChange('height', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-4 p-6 border-t bg-gray-50">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? "Update Product" : "Create Product"}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;